import type React from 'react';
import { useId } from 'react';

import { Box, FieldSet, Label, LabeledControl, Legend, Paragraph, Radio } from '@zillow/constellation-10';

import CurrencyInput from '../../../Widgets/CurrencyInput';

export const REGION1 = 'REGION1';
export const REGION2 = 'REGION2';

type Props = {
  fixedAmountSelected: boolean;
  fixedAmountValue: number;
  setFixedAmountSelected: () => void;
  setFixedAmountValue: (event: Event | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  setVariableMonthlySelected?: () => void;
  variableMonthlyCostSelected?: boolean;
  noAdditionalFee?: boolean;
  noAdditionalFeeSelected?: boolean;
  setAdditionalFeeSelected?: () => void;
  legendText: string;
};

export default function FixedOrVariableSelector({
  fixedAmountSelected,
  fixedAmountValue,
  setFixedAmountSelected,
  setFixedAmountValue,
  setVariableMonthlySelected,
  variableMonthlyCostSelected,
  noAdditionalFee,
  noAdditionalFeeSelected,
  setAdditionalFeeSelected,
  legendText,
}: Props): JSX.Element {
  const radioGroupName = useId();
  const fixedAmountId = useId();
  const noAdditionalFeeId = useId();
  const variableCostId = useId();

  return (
    <FieldSet required legend={<Legend>{legendText}</Legend>}>
      <LabeledControl
        controlId={fixedAmountId}
        label={<Label>Fixed amount</Label>}
        size="lg"
        control={
          <Radio
            aria-label="Fixed amount"
            name={radioGroupName}
            checked={fixedAmountSelected}
            onChange={setFixedAmountSelected}
          />
        }
      />
      <Box css={{ marginLeft: 'lg' }} data-test={REGION1}>
        <CurrencyInput
          onFocus={setFixedAmountSelected}
          aria-label="fixed amount"
          data-test="fixedAmountValue"
          value={fixedAmountValue || ''}
          onChange={setFixedAmountValue}
          endAdornment="per month"
        />
      </Box>
      {noAdditionalFee ? (
        <LabeledControl
          controlId={noAdditionalFeeId}
          label={<Label>No additional fee for landscaping</Label>}
          size="lg"
          control={
            <Radio
              aria-label="No additional fee for landscaping"
              name={radioGroupName}
              checked={noAdditionalFeeSelected}
              onChange={setAdditionalFeeSelected}
            />
          }
        />
      ) : (
        <LabeledControl
          controlId={variableCostId}
          label={
            <Label>
              Variable monthly cost
              <br />
              <Paragraph css={{ color: 'text.subtle' }}>
                Landlord determines reasonable proportion of actual cost
              </Paragraph>
            </Label>
          }
          size="lg"
          control={
            <Radio
              aria-label="Variable monthly cost"
              data-test={REGION2}
              name={radioGroupName}
              checked={variableMonthlyCostSelected}
              onChange={setVariableMonthlySelected}
            />
          }
        />
      )}
    </FieldSet>
  );
}
